import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  VideoCamera,
  FileText,
  Brain,
  Timer,
  Spinner,
  X
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function toEmbedUrl(url) {
  if (!url) return null;
  // Already an upload
  if (url.includes('/api/files/')) return { type: 'upload', url };
  
  // --- Google Drive ---
  // Formats: drive.google.com/file/d/FILE_ID/view, drive.google.com/open?id=FILE_ID
  let driveId = null;
  const driveMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (driveMatch) driveId = driveMatch[1];
  if (!driveId) {
    const driveOpenMatch = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
    if (driveOpenMatch) driveId = driveOpenMatch[1];
  }
  if (!driveId) {
    const driveIdMatch = url.match(/id=([a-zA-Z0-9_-]+)/);
    if (driveIdMatch && url.includes('drive.google.com')) driveId = driveIdMatch[1];
  }
  if (driveId) {
    return { type: 'drive', url: `https://drive.google.com/file/d/${driveId}/preview` };
  }

  // --- YouTube ---
  let videoId = null;
  const watchMatch = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
  if (watchMatch) videoId = watchMatch[1];
  if (!videoId) {
    const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
    if (shortMatch) videoId = shortMatch[1];
  }
  if (!videoId) {
    const embedMatch = url.match(/youtube(?:-nocookie)?\.com\/embed\/([a-zA-Z0-9_-]{11})/);
    if (embedMatch) videoId = embedMatch[1];
  }
  if (videoId) {
    return { type: 'youtube', url: `https://www.youtube-nocookie.com/embed/${videoId}` };
  }
  
  // Unknown URL - try as iframe
  return { type: 'iframe', url };
}

export function LessonPlayer() {
  const { courseId, moduleId } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [videoProgress, setVideoProgress] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [quizStarted, setQuizStarted] = useState(false);

  useEffect(() => {
    fetchModule();
    fetchCourse();
  }, [moduleId, courseId]);

  useEffect(() => {
    if (quizStarted && timeLeft !== null && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleSubmitQuiz();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [quizStarted, timeLeft]);

  const fetchModule = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/modules/${moduleId}`, {
        withCredentials: true
      });
      setModule(response.data);
      if (response.data.content_type === 'quiz') {
        setShowQuiz(true);
        if (response.data.quiz_data?.time_limit) {
          setTimeLeft(response.data.quiz_data.time_limit);
        }
      }
    } catch (error) {
      console.error('Error fetching module:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/courses/${courseId}`, {
        withCredentials: true
      });
      setCourse(response.data);
    } catch (error) {
      console.error('Error fetching course:', error);
    }
  };

  const updateProgress = async (percent, completed = false) => {
    try {
      await axios.post(`${API_URL}/api/progress`, {
        module_id: moduleId,
        progress_percent: percent,
        completed
      }, { withCredentials: true });
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const handleVideoProgress = () => {
    const newProgress = Math.min(videoProgress + 20, 100);
    setVideoProgress(newProgress);
    updateProgress(newProgress, newProgress === 100);
  };

  const handleMarkComplete = async () => {
    await updateProgress(100, true);
    goToNext();
  };

  const handleStartQuiz = () => {
    setQuizStarted(true);
    // Randomize questions if enabled
    if (module.quiz_data?.randomize) {
      const questions = [...module.quiz_data.questions];
      for (let i = questions.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [questions[i], questions[j]] = [questions[j], questions[i]];
      }
      setModule(prev => ({
        ...prev,
        quiz_data: { ...prev.quiz_data, questions }
      }));
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleSubmitQuiz = async () => {
    setQuizSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/quiz/submit`, {
        module_id: moduleId,
        answers: quizAnswers,
        time_spent: module.quiz_data?.time_limit ? module.quiz_data.time_limit - (timeLeft || 0) : 0
      }, { withCredentials: true });
      setQuizResult(response.data);
    } catch (error) {
      console.error('Error submitting quiz:', error);
    } finally {
      setQuizSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const goToNext = () => {
    if (!course) return;
    const currentIndex = course.modules.findIndex(m => m.id === moduleId);
    if (currentIndex < course.modules.length - 1) {
      navigate(`/learn/${courseId}/${course.modules[currentIndex + 1].id}`);
    } else {
      navigate(`/courses/${courseId}`);
    }
  };

  const goToPrev = () => {
    if (!course) return;
    const currentIndex = course.modules.findIndex(m => m.id === moduleId);
    if (currentIndex > 0) {
      navigate(`/learn/${courseId}/${course.modules[currentIndex - 1].id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A252F] flex items-center justify-center">
        <Spinner size={48} className="animate-spin text-white" />
      </div>
    );
  }

  if (!module) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#1A252F] mb-4">Modulo nao encontrado</h1>
          <Link to={`/courses/${courseId}`}>
            <Button>Voltar ao Curso</Button>
          </Link>
        </div>
      </div>
    );
  }

  const currentIndex = course?.modules?.findIndex(m => m.id === moduleId) || 0;
  const totalModules = course?.modules?.length || 0;

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="lesson-player">
      {/* Top Bar */}
      <div className="bg-white border-b border-[#2C3E50]/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link 
            to={`/courses/${courseId}`}
            className="flex items-center gap-2 text-[#5C6B7A] hover:text-[#2C3E50]"
          >
            <X size={20} />
            <span className="hidden sm:inline">Sair</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#5C6B7A]">
              Módulo {currentIndex + 1} de {totalModules}
            </span>
            <Progress value={(currentIndex + 1) / totalModules * 100} className="w-32 h-2" />
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={goToPrev}
              disabled={currentIndex === 0}
              data-testid="prev-module"
            >
              <ArrowLeft size={18} />
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={goToNext}
              disabled={currentIndex === totalModules - 1}
              data-testid="next-module"
            >
              <ArrowRight size={18} />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Module Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-[#5C6B7A] mb-2">
            {module.content_type === 'video' && <VideoCamera size={20} />}
            {module.content_type === 'pdf' && <FileText size={20} />}
            {module.content_type === 'quiz' && <Brain size={20} />}
            <span>
              {module.content_type === 'video' ? 'Video' : module.content_type === 'pdf' ? 'Documento PDF' : 'Questionario'}
            </span>
          </div>
          <h1 className="text-2xl font-bold text-[#1A252F]">{module.title}</h1>
          <p className="text-[#5C6B7A] mt-1">{module.description}</p>
        </div>

        {/* Content */}
        {module.content_type === 'video' && (() => {
          const embed = toEmbedUrl(module.video_url);
          return (
          <div className="bg-white rounded-xl border border-[#2C3E50]/10 overflow-hidden">
            <div className="video-container">
              {embed?.type === 'upload' ? (
                <video
                  src={embed.url}
                  controls
                  onTimeUpdate={(e) => {
                    const vid = e.target;
                    if (vid.duration) {
                      const pct = Math.round((vid.currentTime / vid.duration) * 100);
                      setVideoProgress(pct);
                    }
                  }}
                  onEnded={() => {
                    setVideoProgress(100);
                    updateProgress(100, true);
                  }}
                  data-testid="video-player"
                />
              ) : embed ? (
                <iframe
                  src={embed.url}
                  title={module.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  referrerPolicy="no-referrer"
                  data-testid="video-iframe"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-[#F8F9FA]">
                  <p className="text-[#5C6B7A]">Nenhum video configurado</p>
                </div>
              )}
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-[#5C6B7A]">Progresso de visualizacao</span>
                <span className="text-sm font-medium text-[#27AE60]">{videoProgress}%</span>
              </div>
              <Progress value={videoProgress} className="mb-4" />
              <Button 
                onClick={handleMarkComplete}
                className="w-full bg-[#27AE60] hover:bg-[#219653]"
                data-testid="mark-complete"
              >
                <CheckCircle size={18} className="mr-2" />
                Marcar como Concluido
              </Button>
            </div>
          </div>
          );
        })()}

        {module.content_type === 'pdf' && (
          <div className="bg-white rounded-xl border border-[#2C3E50]/10 p-6">
            {module.pdf_url && module.pdf_url.includes('/api/files/') ? (
              <iframe
                src={module.pdf_url}
                title={module.title}
                className="w-full rounded-lg border border-[#E8ECF0]"
                style={{ height: '70vh' }}
                data-testid="pdf-viewer"
              />
            ) : (
              <div className="aspect-[4/3] bg-[#F8F9FA] rounded-lg flex items-center justify-center mb-6">
                <p className="text-[#5C6B7A]">Nenhum PDF disponivel</p>
              </div>
            )}
            <Button 
              onClick={handleMarkComplete}
              className="w-full mt-4 bg-[#27AE60] hover:bg-[#219653]"
              data-testid="mark-complete-pdf"
            >
              <CheckCircle size={18} className="mr-2" />
              Marcar como Lido
            </Button>
          </div>
        )}

        {module.content_type === 'quiz' && (
          <div className="bg-white rounded-xl border border-[#2C3E50]/10 p-6">
            {!quizStarted && !quizResult && (
              <div className="text-center py-8">
                <Brain size={64} className="mx-auto text-[#2C3E50] mb-4" />
                <h2 className="text-xl font-semibold text-[#1A252F] mb-2">Pronto para testar seus conhecimentos?</h2>
                <p className="text-[#5C6B7A] mb-2">
                  {module.quiz_data?.questions?.length} perguntas &bull; Nota minima: {module.quiz_data?.passing_score}%
                </p>
                {module.quiz_data?.time_limit && (
                  <p className="text-[#5C6B7A] mb-6 flex items-center justify-center gap-2">
                    <Timer size={18} />
                    Tempo limite: {formatTime(module.quiz_data.time_limit)}
                  </p>
                )}
                <Button 
                  onClick={handleStartQuiz}
                  className="bg-[#2C3E50] hover:bg-[#1A252F]"
                  data-testid="start-quiz"
                >
                  Iniciar Questionario
                </Button>
              </div>
            )}

            {quizStarted && !quizResult && (
              <>
                {timeLeft !== null && (
                  <div className={`sticky top-20 bg-white py-3 mb-6 border-b flex items-center justify-between ${
                    timeLeft < 60 ? 'timer-warning text-[#E74C3C]' : 'text-[#5C6B7A]'
                  }`}>
                    <span className="font-medium">Tempo Restante</span>
                    <span className="font-bold text-lg flex items-center gap-2">
                      <Timer size={20} />
                      {formatTime(timeLeft)}
                    </span>
                  </div>
                )}

                <div className="space-y-8">
                  {module.quiz_data?.questions?.map((question, index) => (
                    <div key={question.id} className="p-4 bg-[#F8F9FA] rounded-lg" data-testid={`question-${question.id}`}>
                      <p className="font-medium text-[#1A252F] mb-4">
                        {index + 1}. {question.question}
                      </p>
                      
                      {question.type === 'multiple_choice' && (
                        <div className="space-y-2">
                          {question.options.map((option, optIndex) => (
                            <label 
                              key={optIndex}
                              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                                quizAnswers[question.id] === option 
                                  ? 'bg-[#2C3E50] text-white' 
                                  : 'bg-white hover:bg-[#F0F2F5]'
                              }`}
                            >
                              <input
                                type="radio"
                                name={question.id}
                                value={option}
                                checked={quizAnswers[question.id] === option}
                                onChange={() => handleAnswerChange(question.id, option)}
                                className="sr-only"
                              />
                              <span>{option}</span>
                            </label>
                          ))}
                        </div>
                      )}

                      {question.type === 'true_false' && (
                        <div className="flex gap-4">
                          {[true, false].map((value) => (
                            <label 
                              key={String(value)}
                              className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg cursor-pointer transition-colors ${
                                quizAnswers[question.id] === value 
                                  ? 'bg-[#2C3E50] text-white' 
                                  : 'bg-white hover:bg-[#F0F2F5]'
                              }`}
                            >
                              <input
                                type="radio"
                                name={question.id}
                                checked={quizAnswers[question.id] === value}
                                onChange={() => handleAnswerChange(question.id, value)}
                                className="sr-only"
                              />
                              <span>{value ? 'Verdadeiro' : 'Falso'}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <Button 
                  onClick={handleSubmitQuiz}
                  disabled={quizSubmitting}
                  className="w-full mt-8 bg-[#27AE60] hover:bg-[#219653]"
                  data-testid="submit-quiz"
                >
                  {quizSubmitting ? (
                    <Spinner size={18} className="animate-spin" />
                  ) : (
                    'Enviar Respostas'
                  )}
                </Button>
              </>
            )}

            {quizResult && (
              <div className="text-center py-8" data-testid="quiz-result">
                <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  quizResult.passed ? 'bg-[#27AE60]' : 'bg-[#E74C3C]'
                }`}>
                  <span className="text-3xl font-bold text-white">{Math.round(quizResult.score)}%</span>
                </div>
                <h2 className={`text-2xl font-bold mb-2 ${
                  quizResult.passed ? 'text-[#27AE60]' : 'text-[#E74C3C]'
                }`}>
                  {quizResult.passed ? 'Parabéns!' : 'Continue Estudando'}
                </h2>
                <p className="text-[#5C6B7A] mb-6">
                  Você acertou {quizResult.correct_count} de {quizResult.total_questions} perguntas.
                  {!quizResult.passed && ` Você precisa de ${quizResult.passing_score}% para passar.`}
                </p>
                
                {quizResult.passed ? (
                  <Button onClick={goToNext} className="bg-[#2C3E50] hover:bg-[#1A252F]">
                    Continuar para o Próximo Módulo
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                ) : (
                  <Button 
                    onClick={() => {
                      setQuizResult(null);
                      setQuizAnswers({});
                      setQuizStarted(false);
                      if (module.quiz_data?.time_limit) {
                        setTimeLeft(module.quiz_data.time_limit);
                      }
                    }}
                    className="bg-[#2C3E50] hover:bg-[#1A252F]"
                  >
                    Tentar Novamente
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default LessonPlayer;
